// ItemMaterialCaptureModal.jsx
// Wizard-style modal that steps through each material item for per-item material capture.
// Used when finishing a service that has material-type items (from templates).

import React, { Component } from 'react';
import { Modal, Button } from 'react-bootstrap';
import MaterialScanningSection from './MaterialScanningSection';
import { completeItemWithMaterials } from '../../services/serviceItemsService';
import InventoryAPI from '../../services/inventoryService';

class ItemMaterialCaptureModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentStep: 0,
      itemMaterials: {},   // { [stepIndex]: { materials: [], scannedData: {} } }
      saving: false,
      error: null
    };
  }

  componentDidUpdate(prevProps) {
    // Reset when items change
    if (prevProps.items !== this.props.items) {
      this.setState({ currentStep: 0, itemMaterials: {}, saving: false, error: null });
    }
  }

  // Store materials from MaterialScanningSection for a given step
  handleMaterialsChange = (stepIndex, materials) => {
    this.setState(prevState => ({
      itemMaterials: {
        ...prevState.itemMaterials,
        [stepIndex]: {
          ...prevState.itemMaterials[stepIndex],
          materials: materials || []
        }
      }
    }));
  };

  // Store scanned data (brand, product, roll) for a given step
  handleMaterialScanned = (stepIndex, scannedData) => {
    this.setState(prevState => ({
      itemMaterials: {
        ...prevState.itemMaterials,
        [stepIndex]: {
          ...prevState.itemMaterials[stepIndex],
          scannedData: scannedData || {}
        }
      }
    }));
  };

  handleNext = () => {
    const { items } = this.props;
    const { currentStep } = this.state;
    if (currentStep < items.length - 1) {
      this.setState({ currentStep: currentStep + 1 });
    }
  };

  handlePrevious = () => {
    const { currentStep } = this.state;
    if (currentStep > 0) {
      this.setState({ currentStep: currentStep - 1 });
    }
  };

  handleFinish = async () => {
    const { items, onComplete, service } = this.props;
    const { itemMaterials } = this.state;

    console.log('[ItemMaterialCaptureModal] handleFinish called');
    console.log('[ItemMaterialCaptureModal] items:', items.map(i => ({ id: i.id, label: i.label })));
    console.log('[ItemMaterialCaptureModal] itemMaterials state:', JSON.stringify(itemMaterials, null, 2));

    this.setState({ saving: true, error: null });

    try {
      // Process each item
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const stepData = itemMaterials[i];
        const materials = stepData?.materials || [];

        // Build material body for this item — only send columns that exist on request_service_tasks
        // (name/brand/barcode are returned via JOINs on GET, not stored directly)
        let materialBody = null;
        if (materials.length > 0) {
          const firstMaterial = materials[0];
          materialBody = {
            actual_product_id: firstMaterial.product_id || null,
            inventory_item_id: firstMaterial.inventory_item_id || null,
            quantity_used: parseFloat(firstMaterial.quantity_used) || null
          };

          // Deduct inventory for each scanned material on this item
          for (const mat of materials) {
            if (mat.inventory_item_id && !mat.isManualEntry) {
              const qty = parseFloat(mat.quantity_used) || 0;
              if (qty > 0) {
                try {
                  await InventoryAPI.deductInventory({
                    itemId: mat.inventory_item_id,
                    quantity: qty,
                    requestId: service?.request_id,
                    requestServiceTaskId: item.id
                  });
                } catch (err) {
                  console.error('Error deducting inventory for item:', item.label, err);
                }
              }
            }
          }
        }

        // Complete the item (with or without material data)
        try {
          await completeItemWithMaterials(item.id, materialBody);
        } catch (err) {
          console.error('Error completing item:', item.label, err);
        }
      }

      onComplete(null);
    } catch (err) {
      console.error('Error in item material capture finish:', err);
      this.setState({ saving: false, error: 'Failed to save material data. Please try again.' });
    }
  };

  render() {
    const { show, onHide, items, categoryConfig, installerId, service } = this.props;
    const { currentStep, saving, error } = this.state;

    if (!items || items.length === 0) return null;

    const currentItem = items[currentStep];
    const isLastStep = currentStep === items.length - 1;
    const isFirstStep = currentStep === 0;

    // Expected quantity: prefer item-level, fall back to service-level (from installer_services pricing)
    const itemExpectedQty = currentItem.expected_quantity || currentItem.expected_qty || null;
    const serviceExpectedQty = service?.expected_quantity || service?.expected_material_qty || null;
    const expectedQty = itemExpectedQty || serviceExpectedQty || null;

    return (
      <Modal show={show} onHide={onHide} centered size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fa-sharp fa-regular fa-cube mr-2"></i>
            Material Collection &mdash; Item {currentStep + 1} of {items.length}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <div className="alert alert-danger mb-3">
              <i className="fa-sharp fa-regular fa-exclamation-triangle mr-2"></i>
              {error}
            </div>
          )}

          {/* Item context header */}
          <div className="mb-3 p-2 bg-light rounded">
            <div className="font-weight-bold">
              {service?.service_category ? `${service.service_category} - ` : ''}{currentItem.label}
            </div>
            {currentItem.recommended_product_name && (
              <div className="text-muted small">
                <i className="fa-sharp fa-regular fa-tag mr-1"></i>
                Recommended: {currentItem.recommended_product_brand
                  ? `${currentItem.recommended_product_brand} - ${currentItem.recommended_product_name}`
                  : currentItem.recommended_product_name}
              </div>
            )}
          </div>

          {/* Material scanning for current item */}
          <MaterialScanningSection
            key={`item-scan-${currentStep}`}
            installerId={installerId}
            categoryId={categoryConfig?.category_id}
            categoryName={service?.service_category}
            defaultUnitId={categoryConfig?.default_unit_id}
            expectedQty={expectedQty}
            onMaterialsChange={(materials) => this.handleMaterialsChange(currentStep, materials)}
            onMaterialScanned={(data) => this.handleMaterialScanned(currentStep, data)}
          />

          {/* Step indicator dots */}
          {items.length > 1 && (
            <div className="text-center mt-3">
              {items.map((_, idx) => (
                <span
                  key={idx}
                  className={`d-inline-block mx-1 rounded-circle ${idx === currentStep ? 'bg-primary' : 'bg-secondary'}`}
                  style={{ width: 8, height: 8, opacity: idx === currentStep ? 1 : 0.3 }}
                />
              ))}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          {/* Skip / Previous */}
          <div className="mr-auto">
            {!isFirstStep && (
              <Button variant="outline-secondary" onClick={this.handlePrevious} disabled={saving}>
                <i className="fa-sharp fa-regular fa-arrow-left mr-1"></i> Previous
              </Button>
            )}
          </div>

          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>

          {isLastStep ? (
            <Button variant="success" onClick={this.handleFinish} disabled={saving}>
              {saving ? (
                <>
                  <i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fa-sharp fa-regular fa-check mr-2"></i>
                  Finish
                </>
              )}
            </Button>
          ) : (
            <Button variant="primary" onClick={this.handleNext} disabled={saving}>
              Next Item <i className="fa-sharp fa-regular fa-arrow-right ml-1"></i>
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    );
  }
}

export default ItemMaterialCaptureModal;
